import { CheckCircleIcon, XCircleIcon, InformationCircleIcon, ExclamationTriangleIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { clsx } from 'clsx'
import type { Toast, ToastType } from '@/hooks/useToast.types'

interface ToastContainerProps {
  toasts: Toast[]
  onHideToast: (id: string) => void
}

export function ToastContainer({ toasts, onHideToast }: ToastContainerProps) {
  const getIcon = (type: ToastType) => {
    switch (type) {
      case 'success':
        return <CheckCircleIcon className="w-5 h-5 text-green-500" />
      case 'error':
        return <XCircleIcon className="w-5 h-5 text-red-500" />
      case 'warning':
        return <ExclamationTriangleIcon className="w-5 h-5 text-yellow-500" />
      case 'info':
        return <InformationCircleIcon className="w-5 h-5 text-blue-500" />
      default:
        return <InformationCircleIcon className="w-5 h-5 text-blue-500" />
    }
  }

  const getColors = (type: ToastType) => {
    switch (type) {
      case 'success':
        return 'bg-green-50 dark:bg-green-900/30 border-green-200 dark:border-green-800'
      case 'error':
        return 'bg-red-50 dark:bg-red-900/30 border-red-200 dark:border-red-800'
      case 'warning':
        return 'bg-yellow-50 dark:bg-yellow-900/30 border-yellow-200 dark:border-yellow-800'
      case 'info':
        return 'bg-blue-50 dark:bg-blue-900/30 border-blue-200 dark:border-blue-800'
      default:
        return 'bg-blue-50 dark:bg-blue-900/30 border-blue-200 dark:border-blue-800'
    }
  }
  
  const getDismissButtonColors = (type: ToastType) => {
    switch (type) {
      case 'success':
        return 'bg-green-600/10 hover:bg-green-600/20 dark:bg-green-400/10 dark:hover:bg-green-400/20 text-green-700 hover:text-green-900 dark:text-green-300 dark:hover:text-green-100'
      case 'error':
        return 'bg-red-600/10 hover:bg-red-600/20 dark:bg-red-400/10 dark:hover:bg-red-400/20 text-red-700 hover:text-red-900 dark:text-red-300 dark:hover:text-red-100'
      case 'warning':
        return 'bg-yellow-600/10 hover:bg-yellow-600/20 dark:bg-yellow-400/10 dark:hover:bg-yellow-400/20 text-yellow-700 hover:text-yellow-900 dark:text-yellow-300 dark:hover:text-yellow-100'
      case 'info':
        return 'bg-blue-600/10 hover:bg-blue-600/20 dark:bg-blue-400/10 dark:hover:bg-blue-400/20 text-blue-700 hover:text-blue-900 dark:text-blue-300 dark:hover:text-blue-100'
      default:
        return 'bg-gray-600/10 hover:bg-gray-600/20 dark:bg-gray-400/10 dark:hover:bg-gray-400/20 text-gray-700 hover:text-gray-900 dark:text-gray-300 dark:hover:text-gray-100'
    }
  }

  if (toasts.length === 0) return null

  return (
    <div className="fixed top-4 right-4 z-50 space-y-4 max-w-md">
      {toasts.map((toast) => (
        <div
          key={toast.id}
          className={clsx(
            'flex items-start p-4 rounded-lg shadow-lg border animate-in slide-in-from-right-full min-w-[320px]',
            getColors(toast.type)
          )}
        >
          <div className="flex-shrink-0 mr-3">
            {getIcon(toast.type)}
          </div>
          <div className="flex-1">
            <h4 className="text-sm font-medium text-gray-900 dark:text-white">
              {toast.title}
            </h4>
            {toast.message && (
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                {toast.message}
              </p>
            )}
          </div>
          <button 
            type="button"
            onClick={() => { onHideToast(toast.id) }}
            className={clsx(
              "flex-shrink-0 ml-3 p-1.5 rounded-md transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 dark:focus:ring-offset-gray-800",
              getDismissButtonColors(toast.type)
            )}
            aria-label="Dismiss notification"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>
      ))}
    </div>
  )
}